import { parseFullName } from "parse-full-name";

import type { AuthPhysician } from "@elation/api-client/src/stitch-web/models/authPhysician";
import type { Physician } from "@elation/api-client/src/stitch-web/models/physician";
import { PhysicianSpecialty } from "@elation/api-client/src/stitch-web/models/physicianSpecialty";

import { compactStrings, humanizeIdentifier } from "@src/shared/utils/strings";

import type { PhysicianProfileFormValues } from "./physician.schemas";

type MaybePhysician = Physician | AuthPhysician | null | undefined;

export const PhysicianEntity = {
  initials(physician: MaybePhysician): string | null {
    if (!physician) return null;
    const parsed = parseFullName(
      compactStrings([physician.firstName, physician.lastName]).join(" "),
    );

    return compactStrings([parsed.first, parsed.last])
      .map((part) => part.at(0))
      .join("")
      .toUpperCase();
  },

  fullName(physician: MaybePhysician): string | null {
    if (!physician) return null;
    return compactStrings([
      physician.firstName,
      physician.middleName,
      physician.lastName,
    ]).join(" ");
  },

  credentialLabel(physician: MaybePhysician): string | null {
    return physician?.credentials?.trim() || null;
  },

  displayName(physician: MaybePhysician): string | null {
    const name = PhysicianEntity.fullName(physician);
    if (!name) return null;
    const credentials = PhysicianEntity.credentialLabel(physician);

    return credentials ? `${name}, ${credentials}` : name;
  },

  isSelectable(physician: MaybePhysician): boolean {
    if (!physician) return false;
    return physician.isActive;
  },

  specialtyLabel(physician: MaybePhysician): string | null {
    if (!physician) return null;
    return physician.specialty
      ? humanizeIdentifier(physician.specialty)
      : "Unknown";
  },

  licenseLabel(physician: MaybePhysician): string | null {
    const license = physician?.license?.trim();
    if (!license) return null;

    const state = physician?.licenseState?.trim();
    return state ? `${state} ${license}` : license;
  },

  isNpiVerified(physician: MaybePhysician): boolean {
    return Boolean(physician?.npi?.trim());
  },

  specialtyOptions(): string[] {
    return Object.values(PhysicianSpecialty);
  },

  normalizeSpecialty(specialty: string | null | undefined): string {
    if (!specialty?.trim()) return "";

    const options = Object.values(PhysicianSpecialty);
    const exactMatch = options.find(
      (option) => option.toLowerCase() === specialty.toLowerCase(),
    );
    if (exactMatch) return exactMatch;

    const humanized = humanizeIdentifier(specialty);
    return (
      options.find(
        (option) => option.toLowerCase() === humanized.toLowerCase(),
      ) ?? humanized
    );
  },

  toProfileFormValues(physician: MaybePhysician): PhysicianProfileFormValues {
    return {
      firstName: physician?.firstName ?? "",
      middleName: physician?.middleName ?? "",
      lastName: physician?.lastName ?? "",
      specialty: PhysicianEntity.normalizeSpecialty(physician?.specialty),
    };
  },
} as const;
